import React, { useState, useMemo } from 'react';
import HedgingResults from '../../StockAnalyzer/HedgingResults';
import { runHedging } from '../../../services/api';
import { formatCurrency } from '../../../utils/contractCalculations';
import { isStockRow } from '../portfolioConstants';

/**
 * Hedge / Protect for one holding.
 *
 * The Stock Analyzer's HedgingForm is half manual holdings entry — ticker,
 * expiration, strike, quantity, cost basis, typed in row by row. The Portfolio
 * already knows all of that, so this keeps only the protection-goal half and
 * shows the positions read-only.
 *
 * The payload matches HedgingForm's exactly (see its handleSubmit), so the
 * backend and HedgingResults are reused untouched.
 *
 * Limitation: HedgingHoldingSerializer requires option_type to be call or put,
 * so a share position cannot be expressed. A stock-only ticker gets an
 * explanation instead of a broken request.
 */
export default function PortfolioHedgePanel({ ticker, contracts = [] }) {
  const [params, setParams] = useState({
    target_move_pct: -10,
    horizon_days: 30,
    loss_cap: '',
    loss_cap_type: 'dollar',
    trim_pct: 15,
  });
  const [data, setData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Only option positions on this ticker can be hedged by the service.
  const optionPositions = useMemo(
    () => contracts.filter((c) => !isStockRow(c) && c.strike && c.expiration),
    [contracts]
  );
  const stockPositions = useMemo(() => contracts.filter(isStockRow), [contracts]);

  const setParam = (key, value) => setParams((prev) => ({ ...prev, [key]: value }));

  const handleRun = async () => {
    if (!params.loss_cap || Number(params.loss_cap) <= 0) {
      setError('Enter a loss cap greater than 0.');
      return;
    }
    setError('');
    setProcessing(true);
    setData(null);

    try {
      const owned_contracts = optionPositions.map((c) => {
        const contract = {
          ticker: c.ticker,
          option_type: c.option_type,
          strike: Number(c.strike),
          expiration: c.expiration,
          quantity: Number(c.number_of_contracts) || 1,
        };
        if (c.average_cost_per_contract != null) {
          contract.cost_basis = Number(c.average_cost_per_contract);
        }
        return contract;
      });

      const response = await runHedging({
        owned_contracts,
        target_move_pct: Number(params.target_move_pct),
        horizon_days: Number(params.horizon_days) || 30,
        loss_cap: Number(params.loss_cap),
        loss_cap_type: params.loss_cap_type,
        trim_fraction: (Number(params.trim_pct) || 0) / 100,
      });
      setData(response.data?.hedging_results || null);
    } catch (err) {
      console.error('Hedging analysis failed:', err);
      setError(err.response?.data?.error || 'Hedge analysis failed.');
    } finally {
      setProcessing(false);
    }
  };

  if (optionPositions.length === 0) {
    return (
      <div className="text-sm text-muted italic py-6">
        {stockPositions.length > 0 ? (
          <>
            You hold {ticker} as shares only. The hedge tool models protective puts against
            option positions, so it can't cover a share holding yet — add an option position
            on {ticker}, or use the Stock Analyzer's Hedging tab directly.
          </>
        ) : (
          <>No {ticker} option positions to hedge.</>
        )}
      </div>
    );
  }

  const inputCls = 'input';
  const labelCls = 'filter-heading';

  return (
    <div className="space-y-4">
      {/* Positions being hedged — auto-filled, not editable. This is the whole
          reason the tab lives in the Portfolio rather than the Stock Analyzer. */}
      <div>
        <label className={labelCls}>POSITIONS BEING HEDGED</label>
        <div className="mt-1 space-y-1">
          {optionPositions.map((c) => (
            <div
              key={c.id}
              className="pf-inset text-xs text-[color:var(--pf-label)] flex flex-wrap items-center gap-x-3"
            >
              <span className="font-semibold text-brand-white">
                {c.ticker} {String(c.position_type || 'buy').toUpperCase()}{' '}
                {String(c.option_type || '').toUpperCase()} ${c.strike}
              </span>
              <span className="text-[color:var(--pf-muted)]">exp {c.expiration}</span>
              <span className="text-[color:var(--pf-muted)]">
                {c.number_of_contracts || 1} contract
                {(c.number_of_contracts || 1) === 1 ? '' : 's'}
              </span>
              {c.average_cost_per_contract != null && (
                <span className="text-[color:var(--pf-muted)]">
                  @ {formatCurrency(c.average_cost_per_contract)}
                </span>
              )}
            </div>
          ))}
        </div>
        {stockPositions.length > 0 && (
          <p className="text-xs text-muted mt-1 italic">
            Your {ticker} share position isn't included — the hedge model covers option
            positions only.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className={labelCls}>DOWNSIDE MOVE (%)</label>
          <input
            type="number"
            className={inputCls}
            value={params.target_move_pct}
            onChange={(e) => setParam('target_move_pct', e.target.value)}
          />
          <p className="text-xs text-muted mt-1">e.g. −10 for a 10% drop</p>
        </div>

        <div>
          <label className={labelCls}>HORIZON (DAYS)</label>
          <input
            type="number"
            min="1"
            max="365"
            className={inputCls}
            value={params.horizon_days}
            onChange={(e) => setParam('horizon_days', e.target.value)}
          />
          <p className="text-xs text-muted mt-1">How long to stay protected</p>
        </div>

        <div>
          <label className={labelCls}>MAX ACCEPTABLE LOSS</label>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              className={inputCls}
              value={params.loss_cap}
              onChange={(e) => setParam('loss_cap', e.target.value)}
              placeholder="e.g. 2000"
            />
            <select
              className={inputCls}
              style={{ maxWidth: '90px' }}
              value={params.loss_cap_type}
              onChange={(e) => setParam('loss_cap_type', e.target.value)}
            >
              <option value="dollar">$</option>
              <option value="percent">%</option>
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>TRIM ALTERNATIVE (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            className={inputCls}
            value={params.trim_pct}
            onChange={(e) => setParam('trim_pct', e.target.value)}
          />
          <p className="text-xs text-muted mt-1">Compare against selling this much</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-500 text-red-300 px-4 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={handleRun}
        disabled={processing}
        className="btn-primary text-sm"
      >
        {processing ? 'Analyzing...' : `Find a hedge for ${ticker}`}
      </button>

      {data && <HedgingResults data={data} />}
    </div>
  );
}
